import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { AuthLayout } from "@/components/AuthLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2 } from "lucide-react";

export default function AcceptInvitePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [preview, setPreview] = useState(null);
  const [full_name, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/tenant/invites/${token}/preview`);
        setPreview(data);
      } catch (e) {
        setPreview({ valid: false });
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/tenant/invites/accept", { token, full_name, password });
      navigate("/login?invited=1");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <AuthLayout title="Loading invite">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Checking your invite…
        </div>
      </AuthLayout>
    );
  }

  if (!preview?.valid) {
    return (
      <AuthLayout title="Invite unavailable" subtitle="This invite is expired, already used, or was cancelled.">
        <Alert variant="destructive" data-testid="invite-invalid">
          <AlertDescription>Ask your admin to resend the invite.</AlertDescription>
        </Alert>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={`Join ${preview.tenant_name || "your team"}`}
      subtitle={`You've been invited as ${preview.roles.join(", ")}. Pick a password to activate your account.`}
    >
      <form onSubmit={onSubmit} className="space-y-4" data-testid="invite-form">
        <div className="rounded-lg border bg-accent/40 p-3 text-sm">
          <div className="text-muted-foreground">Invited email</div>
          <div className="font-medium">{preview.email}</div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="full_name">Full name</Label>
          <Input id="full_name" required value={full_name} onChange={(e) => setFullName(e.target.value)} data-testid="invite-full-name" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="password">Password <span className="text-muted-foreground">(≥8 chars)</span></Label>
          <Input id="password" type="password" minLength={8} required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="invite-password" />
        </div>
        {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}
        <Button type="submit" className="w-full" disabled={busy} data-testid="invite-submit">
          {busy && <Loader2 className="h-4 w-4 animate-spin" />} Accept & activate
        </Button>
      </form>
    </AuthLayout>
  );
}
